var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import React, { useState } from 'react';
import { Autocomplete, autocompleteClasses, Chip, createFilterOptions, TextField } from '@mui/material';
import ChipsCollection from '../chipsCollection/ChipsCollection';
import TagChip from '../tagChip/components/TagChip';
import { uniqWith } from 'lodash';
import { AutocompleteDropdown } from './AutocompleteDropdown';
import AutocompleteSelectorListItem from './AutocompleteSelectorListItem';
import classNames from 'classnames';
var AutocompleteSelector = function (_a) {
    var label = _a.label, filterText = _a.filterText, setFilterText = _a.setFilterText, options = _a.options, _b = _a.optionLabelKey, optionLabelKey = _b === void 0 ? 'name' : _b, groupBy = _a.groupBy, selectedOptions = _a.selectedOptions, setSelectedOptions = _a.setSelectedOptions, isOptionEqualToValue = _a.isOptionEqualToValue, focusOnMount = _a.focusOnMount, useTagChips = _a.useTagChips, isLoading = _a.isLoading, disabled = _a.disabled, totalOptions = _a.totalOptions, allowSelectAll = _a.allowSelectAll;
    var _c = useState(false), isFocused = _c[0], setIsFocused = _c[1];
    var getOptionLabel = function (option) { return option[optionLabelKey]; };
    var isOptionSelected = function (option) { return isOptionEqualToValue
        ? selectedOptions.some(function (selectedOption) { return isOptionEqualToValue(option, selectedOption); })
        : selectedOptions.includes(option); };
    var handleInputTextChange = function (event, newFilterText, reason) {
        if (reason !== 'reset') {
            setFilterText(newFilterText);
        }
    };
    var handleSelectedOptionsChange = function (event, newSelectedOptions, reason) {
        var _a;
        var isRemovingViaBackspace = reason === 'removeOption' && ((_a = event) === null || _a === void 0 ? void 0 : _a.code) === 'Backspace';
        if (!isRemovingViaBackspace) {
            setSelectedOptions(newSelectedOptions);
        }
    };
    var renderTags = function (value, getTagProps) { return React.createElement(ChipsCollection, { items: value, chipRenderer: function (item, i) {
            var ChipComponent = useTagChips ? TagChip : Chip;
            return React.createElement(ChipComponent, __assign({ label: getOptionLabel(item) }, getTagProps({ index: i })));
        } }); };
    var onGroupHeaderMouseEnter = function (e) {
        // remove focused class from all options
        document
            .querySelectorAll("." + autocompleteClasses.option + "." + autocompleteClasses.focused)
            .forEach(function (focusedOption) { return focusedOption.classList.remove(autocompleteClasses.focused); });
        e.currentTarget.classList.add(autocompleteClasses.focused);
    };
    var onGroupHeaderMouseLeave = function (e) {
        e.currentTarget.classList.remove(autocompleteClasses.focused);
    };
    var renderGroup = !groupBy ? undefined : function (_a) {
        var key = _a.key, group = _a.group, children = _a.children;
        var groupedOptions = options.filter(function (option) { return groupBy(option) === group; });
        var allGroupedOptionsSelected = groupedOptions.every(isOptionSelected);
        var noGroupedOptionsSelected = !groupedOptions.some(isOptionSelected);
        return React.createElement("div", { key: key },
            React.createElement(AutocompleteSelectorListItem, { checked: allGroupedOptionsSelected, indeterminate: !allGroupedOptionsSelected && !noGroupedOptionsSelected, label: group, filterText: filterText, isGroupHeader: true, onClick: function () { return handleSelectDeselectAllClick(groupedOptions, allGroupedOptionsSelected); }, className: classNames(autocompleteClasses.option), "aria-selected": allGroupedOptionsSelected, onMouseEnter: onGroupHeaderMouseEnter, onMouseLeave: onGroupHeaderMouseLeave }),
            children);
    };
    var renderOption = function (liProps, option, _a) {
        var selected = _a.selected;
        var optionKey = !groupBy ? getOptionLabel(option) : groupBy(option) + "-" + getOptionLabel(option);
        return React.createElement(AutocompleteSelectorListItem, __assign({ key: optionKey, checked: selected, label: getOptionLabel(option), filterText: filterText }, liProps));
    };
    var handleSelectDeselectAllClick = function (changedOptions, isDeselecting) {
        var isEqual = isOptionEqualToValue
            ? isOptionEqualToValue
            : function (a, b) { return a === b; };
        var isChanged = function (option) { return changedOptions.findIndex(function (q) { return isEqual(option, q); }) !== -1; };
        var newValue = isDeselecting
            ? selectedOptions.filter(function (option) { return !isChanged(option); })
            : uniqWith(__spreadArray(__spreadArray([], selectedOptions, true), changedOptions, true), isEqual);
        setSelectedOptions(newValue);
    };
    var filterOptionsFn = createFilterOptions();
    var filteredOptions = filterOptionsFn(options, { inputValue: filterText, getOptionLabel: getOptionLabel });
    var autocompleteDropdownProps = {
        options: options,
        totalOptions: totalOptions,
        allowSelectAll: allowSelectAll,
        selectedOptions: selectedOptions,
        filteredOptions: filteredOptions,
        onSelectAllClick: handleSelectDeselectAllClick
    };
    var optionsSortedByGroups = !groupBy ? options : options.sort(function (a, b) { return -groupBy(b).localeCompare(groupBy(a)); });
    return (React.createElement("div", { className: 'autocompleteSelector' },
        React.createElement(Autocomplete, { multiple: true, disableCloseOnSelect: true, openOnFocus: true, options: optionsSortedByGroups, getOptionLabel: getOptionLabel, groupBy: groupBy, renderGroup: renderGroup, inputValue: isFocused ? filterText : '', onInputChange: handleInputTextChange, value: selectedOptions, onChange: handleSelectedOptionsChange, renderInput: function (params) { return React.createElement(TextField, __assign({}, params, { autoFocus: focusOnMount, label: label })); }, renderTags: renderTags, renderOption: renderOption, onFocus: function () {
                setFilterText('');
                setIsFocused(true);
            }, onBlur: function () {
                setIsFocused(false);
            }, isOptionEqualToValue: isOptionEqualToValue, noOptionsText: isLoading ? 'Searching' : "No results found", disabled: disabled, autoHighlight: true, ListboxComponent: AutocompleteDropdown, ListboxProps: autocompleteDropdownProps, filterOptions: filterOptionsFn })));
};
export default AutocompleteSelector;
//# sourceMappingURL=AutocompleteSelector.js.map